/* =====================================================================
 * @auw/i18n — agent profile registry.
 *
 * Maps each "agent profile" (a B2C-facing hero card on the sidebar /
 * storage / DM surfaces) to:
 *   - its display label via an i18n key
 *   - the feature flag that controls whether the tenant exposes it
 *   - the workspace mode it points at
 *
 * Lives here (inside `@auw/i18n`) instead of `@auw/workspace-utils`
 * because every label lookup goes through `i18n.t(...)`. Keeping the
 * utility next to the resources avoids a circular dependency with the
 * future `@auw/workspace-ui` / `@auw/workspace-features` packages.
 * ===================================================================== */

import { useFeatureFlags, type FeatureId } from '@auw/feature-flags';
import type { WorkspaceMode } from '@auw/workspace-utils';
import type { DMAgentType } from '@auw/workspace-utils/types/dm';

import i18n from './bootstrap';

export const AGENT_PROFILE_IDS = [
  'agent',
  'document',
] as const;

export type AgentProfileId = (typeof AGENT_PROFILE_IDS)[number];

export interface AgentProfileMeta {
  id: AgentProfileId;
  label: string;
  letter: string;
  color: string;
}

export function getAgentProfileMeta(): AgentProfileMeta[] {
  return [
    { id: 'agent', label: i18n.t('common:agentProfile.agent'), letter: 'A', color: '#D4915A' },
    { id: 'document', label: i18n.t('common:agentProfile.document'), letter: 'D', color: '#6B9EC2' },
  ];
}

/**
 * Map a B2C-facing agent profile id to the feature-flag id that controls
 * its availability.  Kept private so callers go through the hook / helper
 * below and we keep a single source of truth for the mapping.
 */
function agentProfileIdToModeFeature(id: AgentProfileId): FeatureId {
  switch (id) {
    case 'agent':    return 'mode.agent';
    case 'document': return 'mode.document';
  }
}

/**
 * React hook — returns whether an agent profile is reachable under the
 * active tenant.  Replaces the Phase-3 `isAgentProfileVisible` pure
 * function; callers must be inside a `<FeatureFlagProvider>`.
 */
export function useIsAgentProfileVisible(id: AgentProfileId): boolean {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(agentProfileIdToModeFeature(id));
}

/**
 * Loop-friendly predicate: same mapping as {@link useIsAgentProfileVisible}
 * but expressed as a plain callable so a component can pass its
 * `useFeatureFlags().isEnabled` closure into `.filter()`.
 */
export function agentProfileVisible(
  id: AgentProfileId,
  isEnabled: (featureId: FeatureId) => boolean,
): boolean {
  return isEnabled(agentProfileIdToModeFeature(id));
}

/** @deprecated Use getAgentProfileMeta() for up-to-date translations */
export const AGENT_PROFILE_META: AgentProfileMeta[] = [
  { id: 'agent', label: '통합 에이전트', letter: 'A', color: '#D4915A' },
  { id: 'document', label: '브레인 노트', letter: 'D', color: '#6B9EC2' },
];

const AGENT_PROFILE_ID_SET = new Set<string>(AGENT_PROFILE_IDS);

export function isAgentProfileId(value: string): value is AgentProfileId {
  return AGENT_PROFILE_ID_SET.has(value);
}

export function workspaceModeToAgentProfileId(mode: WorkspaceMode): AgentProfileId | null {
  switch (mode) {
    case 'agent':
    case 'document':
      return mode;
    default:
      return null;
  }
}

export function dmAgentTypeToAgentProfileId(type: DMAgentType): AgentProfileId | null {
  switch (type) {
    case 'system_app':
      return 'agent';
    default:
      return null;
  }
}
